import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatLocalDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Clock, User, Star, History, FileText, ClipboardList } from "lucide-react";
import { Loader2 } from "lucide-react";
import type { EventReview, TalentInvoice, Talent, ShowCallSheet } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ShowsPage() {
  const { user } = useAuth();
  
  const { data: talent, isLoading: talentLoading } = useQuery<Talent>({
    queryKey: ["/api/talent/by-user", user?.id],
    enabled: !!user?.id,
  });
  
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<TalentInvoice[]>({
    queryKey: ["/api/talent", talent?.id, "invoices"],
    enabled: !!talent?.id,
  });
  
  const { data: reviews = [] } = useQuery<EventReview[]>({
    queryKey: ["/api/talent", talent?.id, "reviews"],
    enabled: !!talent?.id,
  });

  const { data: callSheets = [] } = useQuery<ShowCallSheet[]>({
    queryKey: ["/api/call-sheets", "byTalent", talent?.id],
    queryFn: async () => {
      const r = await fetch(`/api/call-sheets?talentId=${encodeURIComponent(talent!.id)}`, { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!talent?.id,
  });

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const upcomingShows = invoices
    .filter((inv) => inv.dateOfEvent && new Date(inv.dateOfEvent) >= now)
    .sort((a, b) => {
      const dateA = a.dateOfEvent ? new Date(a.dateOfEvent).getTime() : 0;
      const dateB = b.dateOfEvent ? new Date(b.dateOfEvent).getTime() : 0;
      return dateA - dateB;
    });
    
  const pastShows = invoices
    .filter((inv) => inv.dateOfEvent && new Date(inv.dateOfEvent) < now)
    .sort((a, b) => {
      const dateA = a.dateOfEvent ? new Date(a.dateOfEvent).getTime() : 0;
      const dateB = b.dateOfEvent ? new Date(b.dateOfEvent).getTime() : 0;
      return dateB - dateA;
    });
  
  const reviewsByInvoice = new Map<string, EventReview>();
  reviews.forEach((r) => {
    reviewsByInvoice.set(r.invoiceId, r);
  });

  const formatDate = (date: Date | string | null) =>
    formatLocalDate(date, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const formatShortDate = (date: Date | string | null) =>
    formatLocalDate(date, { month: "short", day: "numeric", year: "numeric" });

  const formatTime = (time: string | null) => {
    if (!time) return "TBD";
    try {
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm} CT`;
    } catch {
      return time;
    }
  };

  const isLoading = talentLoading || invoicesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" data-testid="loader" />
      </div>
    );
  }

  if (!talent) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold" data-testid="text-page-title">My Events</h1>
            <p className="text-sm text-muted-foreground">Your performances and feedback</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground bg-card border border-card-border rounded-lg">
          <User className="w-12 h-12 mb-4 opacity-50" />
          <p className="text-lg font-medium">No Talent Profile</p>
          <p className="text-sm">You need an talent profile to view your shows.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold" data-testid="text-page-title">My Events</h1>
          <p className="text-sm text-muted-foreground">Your performances and feedback</p>
        </div>
      </div>

      <Tabs defaultValue="upcoming" className="w-full">
        <TabsList>
          <TabsTrigger value="upcoming" className="gap-2" data-testid="tab-upcoming">
            <Calendar className="w-4 h-4" />
            Upcoming ({upcomingShows.length})
          </TabsTrigger>
          <TabsTrigger value="past" className="gap-2" data-testid="tab-past">
            <History className="w-4 h-4" />
            Past Events ({pastShows.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-6">
          {upcomingShows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground bg-card border border-card-border rounded-lg">
              <Calendar className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No upcoming events</p>
              <p className="text-sm">Your scheduled performances will appear here.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {upcomingShows.map((invoice) => {
                const sheet = callSheets.find((cs: any) => cs.bookingId === invoice.id || cs.invoiceId === invoice.id);
                return (
                  <Card key={invoice.id} className="hover-elevate" data-testid={`card-upcoming-show-${invoice.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg">{invoice.accountName || "Unnamed Venue"}</CardTitle>
                        <Badge>{invoice.eventRate ? `$${parseFloat(invoice.eventRate).toFixed(0)}` : "TBD"}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span>{formatDate(invoice.dateOfEvent)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span>{formatTime(invoice.eventTime)}</span>
                      </div>
                      {invoice.accountAddress && (
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{invoice.accountAddress}</span>
                        </div>
                      )}
                      {invoice.bookingDetails && (
                        <p className="text-sm text-muted-foreground pt-2 border-t border-border line-clamp-3">
                          {invoice.bookingDetails}
                        </p>
                      )}
                      <div className="pt-2 border-t border-border">
                        {sheet ? (
                          <Link href={`/call-sheet/${sheet.id}`}>
                            <Button variant="outline" size="sm" className="w-full gap-2" data-testid={`button-callsheet-${invoice.id}`}>
                              <ClipboardList className="w-4 h-4" />
                              View Call Sheet
                            </Button>
                          </Link>
                        ) : (
                          <Link href="/artist">
                            <Button variant="ghost" size="sm" className="w-full gap-2 text-muted-foreground" data-testid={`button-callsheets-portal-${invoice.id}`}>
                              <ClipboardList className="w-4 h-4" />
                              Call Sheets
                            </Button>
                          </Link>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-6">
          {pastShows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground bg-card border border-card-border rounded-lg">
              <History className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No past events</p>
              <p className="text-sm">Your completed performances will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pastShows.map((invoice) => {
                const review = reviewsByInvoice.get(invoice.id);
                return (
                  <Card key={invoice.id} data-testid={`card-past-show-${invoice.id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <div className="font-medium" data-testid={`text-show-name-${invoice.id}`}>
                            {invoice.accountName || "Unnamed Show"}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatShortDate(invoice.dateOfEvent)}
                            </span>
                            {invoice.accountAddress && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                <span className="truncate max-w-[200px]">{invoice.accountAddress}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {invoice.eventRate && (
                            <Badge variant="outline">
                              ${parseFloat(invoice.eventRate).toFixed(0)}
                            </Badge>
                          )}
                          
                          {review ? (
                            <Badge className="gap-1" data-testid={`review-${invoice.id}`}>
                              <Star className="w-3.5 h-3.5 fill-current" />
                              <span data-testid={`text-rating-${invoice.id}`}>
                                {review.rating}/10
                              </span>
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1">
                              <FileText className="w-3.5 h-3.5" />
                              No review
                            </Badge>
                          )}
                        </div>
                      </div>
                      {review?.notes && (
                        <p className="text-sm text-muted-foreground mt-2 pt-2 border-t border-border" data-testid={`text-review-notes-${invoice.id}`}>
                          {review.notes}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
